/**
 * TUVI - Matriz de Actividades
 * CRUD para gestionar la matriz base del cotizador
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import * as XLSX from 'xlsx';
import { useNotification } from '../../context/NotificationContext';
import client from '../../services/graphqlClient';
import { useAuth } from '../../context/AuthContext';
import { BACKEND_URL } from '../../config';
import CatalogoFFE from '../CatalogoFFE/CatalogoFFE';
import TranscripcionCotizador from './TranscripcionCotizador';
import './MatrizActividades.css';

const calcularValorFinal = (valorUnitario, incremento, desperdicio, multiplicador) => {
  const vu = parseFloat(valorUnitario) || 0;
  const inc = parseFloat(incremento) || 0;
  const desp = parseFloat(desperdicio) || 1;
  const mult = parseFloat(multiplicador) || 1;
  return vu * (1 + inc / 100) * desp * mult;
};

// Selector de proveedor en línea para la matriz: escribe o pega parte del nombre
// (o el NIT), elige en la lista y se guarda al instante sin recargar. La lista se
// pinta con posición fija para que no la recorte el scroll horizontal de la tabla.
function SelectorProveedor({ valorId, valorNombre, proveedores, onElegir, guardando }) {
  const [abierto, setAbierto] = useState(false);
  const [texto, setTexto] = useState('');
  const [pos, setPos] = useState(null);
  const inputRef = useRef(null);
  const abrir = () => {
    const r = inputRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + 2, left: r.left, width: Math.max(r.width, 360) });
    setTexto('');
    setAbierto(true);
  };
  const cerrar = () => setAbierto(false);
  const q = texto.trim().toLowerCase();
  const opciones = (q ? proveedores.filter(p => p.nombre.toLowerCase().includes(q) || (p.nit || '').includes(q)) : proveedores).slice(0, 25);
  const elegir = (p) => { cerrar(); if (inputRef.current) inputRef.current.blur(); onElegir(p ? p.id : null, p ? p.nombre : ''); };
  return (
    <div className="sel-prov">
      <input
        ref={inputRef}
        className={`sel-prov-input ${valorId ? 'con-valor' : ''}`}
        value={abierto ? texto : (valorNombre || '')}
        placeholder={guardando ? 'Guardando…' : 'Elegir proveedor…'}
        onFocus={abrir}
        onChange={e => setTexto(e.target.value)}
        onBlur={() => setTimeout(cerrar, 150)}
        onKeyDown={e => {
          if (e.key === 'Enter' && opciones[0]) { e.preventDefault(); elegir(opciones[0]); }
          if (e.key === 'Escape') { cerrar(); e.currentTarget.blur(); }
        }}
        disabled={!!guardando}
        title={valorNombre || 'Elegir proveedor'}
      />
      {valorId && !abierto && !guardando && (
        <button type="button" className="sel-prov-x" onMouseDown={e => e.preventDefault()} onClick={() => elegir(null)} title="Quitar proveedor">✕</button>
      )}
      {abierto && pos && (
        <div className="sel-prov-lista" style={{ top: pos.top, left: pos.left, width: pos.width }}>
          {opciones.length === 0 && <div className="sel-prov-vacio">Sin coincidencias</div>}
          {opciones.map(p => (
            <div key={p.id} className={`sel-prov-item ${p.id === valorId ? 'actual' : ''}`} onMouseDown={e => { e.preventDefault(); elegir(p); }}>
              <span className="sel-prov-nombre">{p.nombre}</span>
              <span className={`sel-prov-tag ${p.clasificacion}`}>{p.clasificacion}</span>
            </div>
          ))}
          {opciones.length === 25 && <div className="sel-prov-vacio">Escribe más letras para afinar la lista…</div>}
        </div>
      )}
    </div>
  );
}

const MatrizActividades = () => {
  const { success, error } = useNotification();
  const { tienePermiso, user } = useAuth();
  // Solo con este permiso se pueden aprobar o rechazar las actividades nuevas.
  const puedeAprobar = tienePermiso('documentos', 'matriz-aprobar');

  // Actividades nuevas a la espera de revisión
  const [pendientesCount, setPendientesCount] = useState(0);
  const [modalPend, setModalPend] = useState({ visible: false, filas: [], cargando: false, verRechazadas: false });

  const cargarPendientesCount = useCallback(async () => {
    try {
      const d = await client.query('{ matrizActividadesPendientes { id } }');
      setPendientesCount((d.matrizActividadesPendientes || []).length);
    } catch { /* silencioso */ }
  }, []);

  const cargarPendientes = async (verRechazadas) => {
    setModalPend(m => ({ ...m, cargando: true, verRechazadas }));
    try {
      const d = await client.query(
        'query($r: Boolean) { matrizActividadesPendientes(incluirRechazadas: $r) { id codigo actividad capitulo categoria espacio subcategoria_espacio unidad valor_unitario cantidad_default cantidad_predeterminada incremento_actividades factor_desperdicio factor_multiplicador ficha_ffe enchape req_compra imagen_url imagen_nombre estado motivo_rechazo solicitada_por_id solicitada_por_nombre revisada_por_nombre created_at } }',
        { r: verRechazadas }
      );
      setModalPend(m => ({ ...m, filas: d.matrizActividadesPendientes || [], cargando: false }));
      setPendientesCount((d.matrizActividadesPendientes || []).filter(x => x.estado === 'pendiente').length);
    } catch (e) {
      error('No se pudieron cargar las solicitudes');
      setModalPend(m => ({ ...m, cargando: false }));
    }
  };

  const abrirPendientes = async () => {
    setModalPend({ visible: true, filas: [], cargando: true, verRechazadas: false });
    await cargarPendientes(false);
  };

  const aprobarPendiente = async (fila) => {
    try {
      const r = await client.mutate('mutation($id: Int!) { aprobarMatrizActividad(id: $id) { success message } }', { id: fila.id });
      if (r.aprobarMatrizActividad.success) { success(r.aprobarMatrizActividad.message); cargarActividades(); }
      else error(r.aprobarMatrizActividad.message);
      await cargarPendientes(modalPend.verRechazadas);
    } catch { error('Error al aprobar'); }
  };

  const rechazarPendiente = async (fila) => {
    const motivo = window.prompt(`¿Por qué se rechaza "${fila.actividad}"? (opcional)`);
    if (motivo === null) return;   // canceló
    try {
      const r = await client.mutate('mutation($id: Int!, $m: String) { rechazarMatrizActividad(id: $id, motivo: $m) { success message } }', { id: fila.id, m: motivo });
      if (r.rechazarMatrizActividad.success) success(r.rechazarMatrizActividad.message);
      else error(r.rechazarMatrizActividad.message);
      await cargarPendientes(modalPend.verRechazadas);
    } catch { error('Error al rechazar'); }
  };

  const [vista, setVista] = useState('lista');
  const [mostrarTranscripcion, setMostrarTranscripcion] = useState(false);
  const [actividades, setActividades] = useState([]);
  const [listas, setListas] = useState({ categorias: [], capitulos: [], espacios: [], unidades: [], subcategorias_espacio: [] });
  const [loading, setLoading] = useState(false);
  const [actividadEditando, setActividadEditando] = useState(null);

  // Paginación y filtros
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Búsqueda con debounce para evitar muchas peticiones rápidas (WAF)
  const [buscarInput, setBuscarInput] = useState('');
  const debounceRef = useRef(null);

  // Bulk incremento
  const [incrementoMasivo, setIncrementoMasivo] = useState('');
  const [aplicandoIncremento, setAplicandoIncremento] = useState(false);

  // Import
  const [importando, setImportando] = useState(false);
  const fileInputRef = useRef(null);

  // Relacionar imagen del catálogo FFE: guarda la actividad cuyo modal está abierto
  const [imagenModal, setImagenModal] = useState(null); // { id, codigo, actividad } | null

  const [filtros, setFiltros] = useState({
    buscar: '',
    categoria: '',
    capitulo: '',
    espacio: '',
    activo: '',
    req_compra: ''
  });

  // Proveedores para la columna Proveedor (todos los activos: proveedores,
  // contratistas y administrativos) y la actividad cuyo proveedor se está guardando.
  const [proveedores, setProveedores] = useState([]);
  const [guardandoProv, setGuardandoProv] = useState(null);

  const [formData, setFormData] = useState({
    codigo: '',
    actividad: '',
    categoria: '',
    unidad: 'UND',
    valor_unitario: 0,
    cantidad_predeterminada: '1',
    cantidad_default: 1,
    incremento_actividades: 10,
    factor_desperdicio: 1,
    factor_multiplicador: 1,
    espacio: 'GENERALES',
    subcategoria_espacio: '',
    ficha_ffe: 'NO',
    enchape: 'NO',
    req_compra: 'NO',
    capitulo: '',
    imagen_url: '',
    imagen_nombre: '',
    activo: 1
  });
  // Solicitud rechazada que el usuario esta corrigiendo para reenviarla.
  const [pendienteEditando, setPendienteEditando] = useState(null);
  // Selector de imagen para el formulario de creacion/correccion.
  const [imagenFormModal, setImagenFormModal] = useState(false);


  const cargarListas = useCallback(async () => {
    try {
      const data = await client.query(`{ matrizListas }`);
      if (data.matrizListas) {
        setListas({ success: true, ...data.matrizListas });
      }
    } catch (err) {
      console.error('Error cargando listas:', err);
    }
  }, []);

  const actAbortRef = useRef(null);

  const cargarActividades = useCallback(async () => {
    setLoading(true);
    try {
      const vars = { page, limit: 50 };
      // Si hay búsqueda por texto, no aplicar filtros de categoría para que busque en toda la matriz
      if (filtros.buscar) {
        vars.buscar = filtros.buscar;
      } else {
        if (filtros.capitulo) vars.capitulo = filtros.capitulo;
        if (filtros.categoria) vars.categoria = filtros.categoria;
        if (filtros.espacio) vars.espacio = filtros.espacio;
      }
      // Estos dos aplican siempre, también con búsqueda por texto
      if (filtros.req_compra) vars.req_compra = filtros.req_compra;
      if (filtros.activo === '1' || filtros.activo === '0') vars.activo = filtros.activo === '1';
      const data = await client.query(`query($page: Int, $limit: Int, $capitulo: String, $categoria: String, $espacio: String, $buscar: String, $req_compra: String, $activo: Boolean) { matrizActividadesListar(page: $page, limit: $limit, capitulo: $capitulo, categoria: $categoria, espacio: $espacio, buscar: $buscar, req_compra: $req_compra, activo: $activo) { actividades { id codigo actividad capitulo categoria espacio subcategoria_espacio unidad valor_unitario cantidad_default cantidad_predeterminada incremento_actividades factor_desperdicio factor_multiplicador ficha_ffe enchape req_compra activo imagen_url imagen_nombre proveedor_id proveedor_nombre } total totalPages } }`, vars);

      setActividades(data.matrizActividadesListar.actividades || []);
      setTotal(data.matrizActividadesListar.total || 0);
      setTotalPages(data.matrizActividadesListar.totalPages || 1);
    } catch (err) {
      console.error('Error cargando actividades:', err);
      error('Error al cargar actividades');
    } finally {
      setLoading(false);
    }
  }, [page, filtros, error]);

  useEffect(() => {
    cargarListas();
  }, [cargarListas]);

  useEffect(() => {
    cargarPendientesCount();
  }, [cargarPendientesCount]);

  useEffect(() => {
    cargarActividades();
  }, [cargarActividades]);

  useEffect(() => {
    client.query('{ proveedores { proveedores { id nombre nit clasificacion } } }')
      .then(d => setProveedores((d.proveedores?.proveedores || [])
        .map(p => ({ id: p.id, nombre: p.nombre, nit: p.nit || '', clasificacion: p.clasificacion || 'proveedor' }))
        .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'))))
      .catch(() => {});
  }, []);

  // Guarda el proveedor de una actividad sin recargar: solo se actualiza esa fila.
  const asignarProveedor = async (actividadId, proveedorId, nombre) => {
    setGuardandoProv(actividadId);
    try {
      const r = await client.mutate('mutation($id: Int!, $p: Int) { asignarProveedorMatriz(id: $id, proveedor_id: $p) { success message } }', { id: actividadId, p: proveedorId });
      if (r.asignarProveedorMatriz.success) {
        setActividades(prev => prev.map(a => (a.id === actividadId ? { ...a, proveedor_id: proveedorId, proveedor_nombre: nombre } : a)));
        success(r.asignarProveedorMatriz.message);
      } else {
        error(r.asignarProveedorMatriz.message);
      }
    } catch (e) {
      error('No se pudo guardar el proveedor');
    } finally {
      setGuardandoProv(null);
    }
  };

  // Debounce para la búsqueda (evita muchas peticiones rápidas que disparan el WAF)
  useEffect(() => {
    // Limpiar timeout anterior
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Esperar 500ms antes de actualizar el filtro real
    debounceRef.current = setTimeout(() => {
      setFiltros(prev => {
        if (prev.buscar !== buscarInput) {
          return { ...prev, buscar: buscarInput };
        }
        return prev;
      });
      setPage(1);
    }, 500);

    // Cleanup
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [buscarInput]);

  const handleBuscarChange = (e) => {
    setBuscarInput(e.target.value);
  };

  const handleFiltroChange = (e) => {
    const { name, value } = e.target;
    // El campo buscar usa debounce separado
    if (name === 'buscar') {
      setBuscarInput(value);
      return;
    }
    setFiltros(prev => ({ ...prev, [name]: value }));
    setPage(1);
  };

  const limpiarFiltros = () => {
    setBuscarInput('');
    setFiltros({
      buscar: '',
      categoria: '',
      capitulo: '',
      espacio: '',
      activo: '',
      req_compra: ''
    });
    setPage(1);
  };

  const handleChange = async (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    }));

    // Auto-generar código al seleccionar categoría (solo en modo crear)
    if (name === 'categoria' && value && !actividadEditando) {
      try {
        const data = await client.query(`query($cap: String!) { matrizSiguienteCodigo(capitulo: $cap) }`, { cap: value });
        if (data.matrizSiguienteCodigo) {
          setFormData(prev => ({ ...prev, codigo: data.matrizSiguienteCodigo }));
        }
      } catch (err) {
        // Silencioso
      }
    }
  };

  const resetForm = () => {
    setFormData({
      codigo: '',
      actividad: '',
      categoria: '',
      unidad: 'UND',
      valor_unitario: 0,
      cantidad_predeterminada: '1',
      cantidad_default: 1,
      incremento_actividades: 10,
      factor_desperdicio: 1,
      factor_multiplicador: 1,
      espacio: 'GENERALES',
      subcategoria_espacio: '',
      ficha_ffe: 'NO',
      enchape: 'NO',
      req_compra: 'NO',
      capitulo: '',
      imagen_url: '',
      imagen_nombre: '',
      activo: 1
    });
    setActividadEditando(null);
    setPendienteEditando(null);
  };

  // Abre el formulario cargado con una solicitud rechazada, para corregirla.
  const corregirRechazada = (p) => {
    setPendienteEditando(p);
    setActividadEditando(null);
    setFormData({
      codigo: p.codigo || '', actividad: p.actividad || '', categoria: p.categoria || '',
      unidad: p.unidad || 'UND', valor_unitario: p.valor_unitario || 0,
      cantidad_predeterminada: p.cantidad_predeterminada || '1',
      cantidad_default: p.cantidad_default || 1,
      incremento_actividades: p.incremento_actividades || 0,
      factor_desperdicio: p.factor_desperdicio || 1,
      factor_multiplicador: p.factor_multiplicador || 1,
      espacio: p.espacio || 'GENERALES', subcategoria_espacio: p.subcategoria_espacio || '',
      ficha_ffe: p.ficha_ffe || 'NO',
      enchape: p.enchape || 'NO', req_compra: p.req_compra || 'NO', capitulo: p.capitulo || '',
      imagen_url: p.imagen_url || '', imagen_nombre: p.imagen_nombre || '',
      activo: 1,
    });
    setModalPend(m => ({ ...m, visible: false }));
    setVista('editar');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const input = {
        codigo: formData.codigo, actividad: formData.actividad, capitulo: formData.capitulo || formData.categoria,
        categoria: formData.categoria, espacio: formData.espacio,
        subcategoria_espacio: formData.subcategoria_espacio || '',
        unidad: formData.unidad,
        valor_unitario: parseFloat(formData.valor_unitario) || 0,
        cantidad_default: parseFloat(formData.cantidad_default) || 1,
        cantidad_predeterminada: formData.cantidad_predeterminada || '',
        incremento_actividades: parseFloat(formData.incremento_actividades) || 0,
        factor_desperdicio: parseFloat(formData.factor_desperdicio) || 1,
        factor_multiplicador: parseFloat(formData.factor_multiplicador) || 1,
        ficha_ffe: formData.ficha_ffe || 'NO',
        enchape: formData.enchape || 'NO',
        req_compra: formData.req_compra || 'NO',
        imagen_url: formData.imagen_url || null,
        imagen_nombre: formData.imagen_nombre || null,
        activo: true,
      };

      let data;
      if (pendienteEditando) {
        // Correccion de una solicitud rechazada: vuelve a la cola de aprobacion.
        const r = await client.mutate(`mutation($id: Int!, $input: MatrizActividadInput!) { editarMatrizActividadPendiente(id: $id, input: $input) { success message } }`, { id: pendienteEditando.id, input });
        data = r.editarMatrizActividadPendiente;
      } else if (actividadEditando) {
        const r = await client.mutate(`mutation($id: Int!, $input: MatrizActividadInput!) { editarMatrizActividad(id: $id, input: $input) { success message } }`, { id: actividadEditando.id, input });
        data = r.editarMatrizActividad;
      } else {
        const r = await client.mutate(`mutation($input: MatrizActividadInput!) { crearMatrizActividad(input: $input) { success message } }`, { input });
        data = r.crearMatrizActividad;
      }

      if (data.success) {
        success(pendienteEditando ? data.message : (actividadEditando ? 'Actividad actualizada' : 'Actividad creada'));
        if (pendienteEditando) { cargarPendientesCount(); }
        cargarActividades();
        cargarListas();   // la subcategoría nueva queda en las sugerencias
        setVista('lista');
        resetForm();
      } else {
        error(data.message || 'Error al guardar');
      }
    } catch (err) {
      error('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const editarActividad = (actividad) => {
    setActividadEditando(actividad);
    setFormData({
      codigo: actividad.codigo,
      actividad: actividad.actividad,
      categoria: actividad.categoria,
      unidad: actividad.unidad,
      valor_unitario: parseFloat(actividad.valor_unitario) || 0,
      cantidad_predeterminada: actividad.cantidad_predeterminada || '1',
      cantidad_default: parseFloat(actividad.cantidad_default) || 1,
      incremento_actividades: actividad.incremento_actividades != null ? parseFloat(actividad.incremento_actividades) : 10,
      factor_desperdicio: parseFloat(actividad.factor_desperdicio) || 1,
      factor_multiplicador: parseFloat(actividad.factor_multiplicador) || 1,
      espacio: actividad.espacio || 'GENERALES',
      subcategoria_espacio: actividad.subcategoria_espacio || '',
      ficha_ffe: actividad.ficha_ffe || 'NO',
      enchape: actividad.enchape || 'NO',
      req_compra: actividad.req_compra || 'NO',
      capitulo: actividad.capitulo,
      activo: Number(actividad.activo)
    });
    setVista('editar');
  };

  // Duplicar: abre el modal de creación con TODOS los datos de la actividad
  // origen, pero generando un código nuevo (siguiente consecutivo de su
  // categoría). El usuario edita lo que necesite y guarda como actividad nueva.
  const duplicarActividad = async (actividad) => {
    setActividadEditando(null); // modo CREAR, no editar

    // Generar el siguiente código según la categoría (misma lógica que al crear)
    let nuevoCodigo = '';
    try {
      const data = await client.query(
        `query($cap: String!) { matrizSiguienteCodigo(capitulo: $cap) }`,
        { cap: actividad.categoria }
      );
      nuevoCodigo = data.matrizSiguienteCodigo || '';
    } catch (err) { /* si falla, queda vacío y el usuario lo completa */ }

    setFormData({
      codigo: nuevoCodigo,
      actividad: actividad.actividad,
      categoria: actividad.categoria,
      unidad: actividad.unidad,
      valor_unitario: parseFloat(actividad.valor_unitario) || 0,
      cantidad_predeterminada: actividad.cantidad_predeterminada || '1',
      cantidad_default: parseFloat(actividad.cantidad_default) || 1,
      incremento_actividades: actividad.incremento_actividades != null ? parseFloat(actividad.incremento_actividades) : 10,
      factor_desperdicio: parseFloat(actividad.factor_desperdicio) || 1,
      factor_multiplicador: parseFloat(actividad.factor_multiplicador) || 1,
      espacio: actividad.espacio || 'GENERALES',
      subcategoria_espacio: actividad.subcategoria_espacio || '',
      ficha_ffe: actividad.ficha_ffe || 'NO',
      enchape: actividad.enchape || 'NO',
      req_compra: actividad.req_compra || 'NO',
      capitulo: actividad.capitulo,
      activo: Number(actividad.activo),
    });
    setVista('crear');
  };

  const eliminarActividad = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar esta actividad?')) return;

    try {
      const gqlR = await client.mutate(`mutation($id: Int!) { eliminarMatrizActividad(id: $id) { success message } }`, { id });

      if (gqlR.eliminarMatrizActividad.success) {
        success('Actividad eliminada');
        cargarActividades();
      } else {
        error(gqlR.eliminarMatrizActividad.message || 'Error al eliminar');
      }
    } catch (err) {
      error('Error de conexión');
    }
  };

  // Guarda la imagen seleccionada del catálogo FFE en la actividad de la matriz.
  // url='' limpia la relación. Actualiza el estado local sin recargar toda la lista.
  const guardarImagenRelacionada = async (id, url, nombre) => {
    try {
      const gqlR = await client.mutate(
        `mutation($id: Int!, $url: String, $nombre: String) { relacionarImagenMatriz(id: $id, imagen_url: $url, imagen_nombre: $nombre) { success message } }`,
        { id, url: url || '', nombre: nombre || '' }
      );
      if (gqlR.relacionarImagenMatriz.success) {
        success(gqlR.relacionarImagenMatriz.message);
        setActividades(prev => prev.map(a => a.id === id ? { ...a, imagen_url: url || '', imagen_nombre: nombre || '' } : a));
      } else {
        error(gqlR.relacionarImagenMatriz.message || 'Error al relacionar imagen');
      }
    } catch (err) {
      error('Error de conexión');
    }
  };

  const quitarImagenRelacionada = async (id) => {
    if (!window.confirm('¿Quitar la imagen relacionada de esta actividad?')) return;
    await guardarImagenRelacionada(id, '', '');
  };

  // Aplicar incremento masivo a todas las actividades
  const aplicarIncrementoMasivo = async () => {
    const valor = parseFloat(incrementoMasivo);
    if (isNaN(valor) || valor < 0) {
      error('Ingrese un valor válido (0 o mayor)');
      return;
    }
    if (!window.confirm(`¿Aplicar incremento ${valor}% a TODAS las actividades?`)) return;

    setAplicandoIncremento(true);
    try {
      const gqlR = await client.mutate(`mutation($v: Float!) { bulkIncrementoMatriz(valor: $v) { success message } }`, { v: parseFloat(valor) });
      const data = gqlR.bulkIncrementoMatriz;
      if (data.success) {
        success(`Incremento actualizado a ${valor}% en ${data.actualizados} actividades`);
        setIncrementoMasivo('');
        cargarActividades();
      } else {
        error(data.message || 'Error al aplicar incremento');
      }
    } catch (err) {
      error('Error de conexión');
    } finally {
      setAplicandoIncremento(false);
    }
  };

  // Exportar actividades a Excel
  const exportarExcel = async () => {
    try {
      const gqlR = await client.query(`{ exportarMatrizCompleta }`);
      const data = { success: true, actividades: gqlR.exportarMatrizCompleta || [] };
      if (!data.actividades.length) {
        error(data.message || 'Error al exportar');
        return;
      }

      const cols = ['codigo', 'actividad', 'categoria', 'capitulo', 'unidad', 'valor_unitario',
        'cantidad_predeterminada', 'incremento_actividades', 'factor_desperdicio',
        'factor_multiplicador', 'espacio', 'subcategoria_espacio', 'ficha_ffe', 'enchape', 'req_compra', 'proveedor', 'activo'];
      const headers = ['Código', 'Actividad', 'Categoría', 'Capítulo', 'Unidad', 'Valor Unitario',
        'Cant. Predeterminada', 'Incremento %', 'Factor Desperdicio',
        'Factor Multiplicador', 'Espacio', 'Subcategoría de Espacio', 'Ficha FFE', 'Enchape', 'Req. Compra', 'Proveedor', 'Activo'];

      const rows = [headers];
      data.actividades.forEach(act => {
        rows.push(cols.map(c => act[c] ?? ''));
      });

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(rows);

      // Anchos de columna
      ws['!cols'] = [
        { wch: 15 }, { wch: 50 }, { wch: 25 }, { wch: 30 }, { wch: 8 },
        { wch: 15 }, { wch: 20 }, { wch: 12 }, { wch: 15 },
        { wch: 15 }, { wch: 15 }, { wch: 25 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 30 }, { wch: 8 }
      ];

      XLSX.utils.book_append_sheet(wb, ws, 'Actividades');
      XLSX.writeFile(wb, `matriz_actividades_${new Date().toISOString().slice(0, 10)}.xlsx`);
      success(`${data.actividades.length} actividades exportadas`);
    } catch (err) {
      error('Error al exportar actividades');
    }
  };

  // Importar actividades desde Excel
  const importarExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImportando(true);
    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });

      if (jsonData.length < 2) {
        error('El archivo no tiene datos suficientes');
        setImportando(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }

      // Mapear headers del Excel a campos de BD
      const headerMap = {
        'Código': 'codigo', 'codigo': 'codigo',
        'Actividad': 'actividad', 'actividad': 'actividad',
        'Categoría': 'categoria', 'categoria': 'categoria',
        'Capítulo': 'capitulo', 'capitulo': 'capitulo',
        'Unidad': 'unidad', 'unidad': 'unidad',
        'Valor Unitario': 'valor_unitario', 'valor_unitario': 'valor_unitario',
        'Cant. Predeterminada': 'cantidad_predeterminada', 'cantidad_predeterminada': 'cantidad_predeterminada',
        'Incremento %': 'incremento_actividades', 'incremento_actividades': 'incremento_actividades',
        'Factor Desperdicio': 'factor_desperdicio', 'factor_desperdicio': 'factor_desperdicio',
        'Factor Multiplicador': 'factor_multiplicador', 'factor_multiplicador': 'factor_multiplicador',
        'Espacio': 'espacio', 'espacio': 'espacio',
        'Subcategoría de Espacio': 'subcategoria_espacio', 'Subcategoria de Espacio': 'subcategoria_espacio',
        'Subcategoría Espacio': 'subcategoria_espacio', 'Subcategoria Espacio': 'subcategoria_espacio',
        'subcategoria_espacio': 'subcategoria_espacio',
        'Ficha FFE': 'ficha_ffe', 'ficha_ffe': 'ficha_ffe',
        'Enchape': 'enchape', 'enchape': 'enchape',
        'Req. Compra': 'req_compra', 'Req Compra': 'req_compra', 'Requiere Compra': 'req_compra',
        'req_compra': 'req_compra',
        'Activo': 'activo', 'activo': 'activo'
      };

      const excelHeaders = jsonData[0].map(h => String(h).trim());
      // Encabezados sin distinguir mayúsculas (ej. "SUBCATEGORÍA DE ESPACIO")
      const headerMapLower = Object.fromEntries(Object.entries(headerMap).map(([k, v]) => [k.toLowerCase(), v]));
      const fieldIndices = {};
      excelHeaders.forEach((h, idx) => {
        const campo = headerMap[h] || headerMapLower[h.toLowerCase()];
        if (campo) fieldIndices[campo] = idx;
      });

      if (!('actividad' in fieldIndices)) {
        error('No se encontró la columna "Actividad" en el archivo');
        setImportando(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }

      // Campos que deben enviarse como número (GraphQL Float). Si vienen como
      // texto del Excel hay que parsearlos, sino GraphQL rechaza ("Float cannot
      // represent non numeric value").
      const camposNumericos = ['valor_unitario', 'incremento_actividades', 'factor_desperdicio', 'factor_multiplicador'];
      const aNumero = (v) => {
        if (v === undefined || v === null || v === '') return null;
        // Quitar separadores de miles y símbolos ($, espacios) por si vienen formateados
        const limpio = String(v).replace(/[^\d.,-]/g, '').replace(/\.(?=\d{3}(\D|$))/g, '').replace(',', '.');
        const n = parseFloat(limpio);
        return isNaN(n) ? null : n;
      };

      const actividades = [];
      let sinCodigo = 0;
      for (let i = 1; i < jsonData.length; i++) {
        const row = jsonData[i];
        if (!row || row.length === 0) continue;
        const obj = {};
        Object.entries(fieldIndices).forEach(([campo, idx]) => {
          const raw = row[idx];
          if (camposNumericos.includes(campo)) {
            obj[campo] = aNumero(raw); // número o null
          } else {
            obj[campo] = raw !== undefined && raw !== null ? String(raw).trim() : '';
          }
        });
        // Ignorar filas completamente vacías (sin descripción de actividad)
        if (!obj.actividad) continue;
        // Si no trae código, se enviará vacío → el backend genera el consecutivo
        if (!obj.codigo) { obj.codigo = ''; sinCodigo++; }
        actividades.push(obj);
      }

      if (actividades.length === 0) {
        error('No se encontraron actividades válidas en el archivo');
        setImportando(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }

      const msgConfirm = `Se encontraron ${actividades.length} actividades`
        + (sinCodigo > 0 ? ` (${sinCodigo} sin código → se crearán con consecutivo automático)` : '')
        + `.\n\nLas que tienen código se actualizan; las nuevas se agregan.\nNo se elimina nada.\n\n¿Continuar con la importación?`;
      if (!window.confirm(msgConfirm)) {
        setImportando(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }

      const gqlR = await client.mutate(`mutation($a: [ActividadImportInput!]!) { importarActividadesMatriz(actividades: $a) { success message } }`, { a: actividades });
      const data = gqlR.importarActividadesMatriz;
      if (data.success) {
        success(`Importación completada: ${data.message}`);
        cargarActividades();
        cargarListas();
      } else {
        error(data.message || 'Error al importar');
      }
    } catch (err) {
      console.error('Error importar:', err);
      error('Error al leer o importar el archivo');
    } finally {
      setImportando(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (mostrarTranscripcion) {
    return <TranscripcionCotizador onVolver={() => setMostrarTranscripcion(false)} />;
  }

  return (
    <div className="matriz-container">
      {/* Actividades nuevas pendientes de aprobación */}
      {modalPend.visible && (
        <div className="pend-overlay" onClick={() => setModalPend(m => ({ ...m, visible: false }))}>
          <div className="pend-modal" onClick={e => e.stopPropagation()}>
            <div className="pend-header">
              <div>
                <h3>Actividades por aprobar</h3>
                <p>
                  Las actividades nuevas no entran a la matriz hasta que se aprueben aquí,
                  así que <strong>no aparecen en el cotizador</strong> mientras tanto.
                </p>
              </div>
              <button className="pend-close" onClick={() => setModalPend(m => ({ ...m, visible: false }))}>&times;</button>
            </div>

            <div className="pend-barra">
              <label className="pend-check">
                <input
                  type="checkbox"
                  checked={modalPend.verRechazadas}
                  onChange={(e) => cargarPendientes(e.target.checked)}
                /> Ver también las rechazadas
              </label>
              {!puedeAprobar && (
                <span className="pend-sin-permiso">
                  Solo puedes consultar: aprobar requiere el permiso “Matriz - Aprobar actividades nuevas”.
                </span>
              )}
            </div>

            <div className="pend-body">
              {modalPend.cargando ? (
                <div className="pend-vacio">Cargando...</div>
              ) : modalPend.filas.length === 0 ? (
                <div className="pend-vacio">
                  <p>No hay actividades esperando aprobación.</p>
                  <p className="pend-nota">Cuando alguien cree una actividad nueva, aparecerá aquí.</p>
                </div>
              ) : (
                <table className="pend-tabla">
                  <thead>
                    <tr>
                      <th>CÓDIGO</th><th>ACTIVIDAD</th><th>CAPÍTULO / CATEGORÍA</th>
                      <th className="pend-num">VALOR</th><th>SOLICITÓ</th><th>ACCIONES</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modalPend.filas.map(f => (
                      <tr key={f.id} className={f.estado === 'rechazada' ? 'pend-fila-rechazada' : ''}>
                        <td className="pend-codigo">{f.codigo}</td>
                        <td>
                          <span className="pend-actividad">{f.actividad}</span>
                          {f.estado === 'rechazada' && (
                            <span className="pend-motivo">
                              Rechazada{f.revisada_por_nombre ? ` por ${f.revisada_por_nombre}` : ''}
                              {f.motivo_rechazo ? `: ${f.motivo_rechazo}` : ''}
                            </span>
                          )}
                        </td>
                        <td className="pend-cat">
                          {f.capitulo}
                          {f.categoria ? <span className="pend-sub">{f.categoria}</span> : null}
                        </td>
                        <td className="pend-num">
                          ${Math.round(Number(f.valor_unitario) || 0).toLocaleString('es-CO')}
                          <span className="pend-sub">{f.unidad}</span>
                        </td>
                        <td className="pend-user">
                          {f.solicitada_por_nombre || '—'}
                          {f.created_at && (
                            <span className="pend-sub">
                              {new Date(f.created_at).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: '2-digit' })}
                            </span>
                          )}
                        </td>
                        <td className="pend-acciones">
                          {f.estado === 'pendiente' ? (
                            <>
                              <button className="pend-btn pend-btn-ok" onClick={() => aprobarPendiente(f)} disabled={!puedeAprobar}>
                                ✓ Aprobar
                              </button>
                              <button className="pend-btn pend-btn-no" onClick={() => rechazarPendiente(f)} disabled={!puedeAprobar}>
                                ✕ Rechazar
                              </button>
                            </>
                          ) : (
                            <>
                              <span className="pend-badge-rech">Rechazada</span>
                              {/* Solo quien la solicitó puede corregirla y reenviarla */}
                              {Number(f.solicitada_por_id) === Number(user?.id) && (
                                <button className="pend-btn pend-btn-corregir" onClick={() => corregirRechazada(f)}>
                                  ✎ Corregir y reenviar
                                </button>
                              )}
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="matriz-header">
        <div className="matriz-header-info">
          <h1>📋 Matriz de Actividades</h1>
          <p>Base de datos para el cotizador de proyectos</p>
        </div>
        <div className="matriz-header-actions">
          {vista === 'lista' ? (
            <>
              <button className="btn-exportar-excel" onClick={exportarExcel}>
                Exportar Excel
              </button>
              <label className="btn-importar-excel">
                {importando ? 'Importando...' : 'Importar Excel'}
                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".xlsx,.xls"
                  onChange={importarExcel}
                  style={{ display: 'none' }}
                  disabled={importando}
                />
              </label>
              <button className="btn-transcripcion" onClick={() => setMostrarTranscripcion(true)}>
                📝 Transcripción - Cotizador Web
              </button>
              <button className="btn-pendientes-matriz" onClick={abrirPendientes} title="Actividades nuevas a la espera de aprobación">
                🕓 Por aprobar
                {pendientesCount > 0 && <span className="pend-badge">{pendientesCount}</span>}
              </button>
              <button className="btn-nuevo" onClick={() => { resetForm(); setVista('crear'); }}>
                + Nueva Actividad
              </button>
            </>
          ) : (
            <button className="btn-volver" onClick={() => { setVista('lista'); resetForm(); }}>
              ← Volver al listado
            </button>
          )}
        </div>
      </div>

      {/* Vista Lista */}
      {vista === 'lista' && (
        <div className="matriz-lista-view">
          {/* Filtros */}
          <div className="matriz-filtros">
            <div className="filtro-buscar">
              <input
                type="text"
                name="buscar"
                placeholder="Buscar por código o actividad..."
                value={buscarInput}
                onChange={handleBuscarChange}
              />
            </div>
            <div className="filtro-select">
              <select name="categoria" value={filtros.categoria} onChange={handleFiltroChange}>
                <option value="">Todas las categorías</option>
                {listas.categorias.map(c => (
                  <option key={c.id} value={c.nombre}>{c.nombre}</option>
                ))}
              </select>
            </div>
            <div className="filtro-select">
              <select name="capitulo" value={filtros.capitulo} onChange={handleFiltroChange}>
                <option value="">Todos los capítulos</option>
                {listas.capitulos.map(c => (
                  <option key={c.id} value={c.nombre}>{c.nombre}</option>
                ))}
              </select>
            </div>
            <div className="filtro-select">
              <select name="espacio" value={filtros.espacio} onChange={handleFiltroChange}>
                <option value="">Todos los espacios</option>
                {listas.espacios.map(e => (
                  <option key={e.id} value={e.nombre}>{e.nombre}</option>
                ))}
              </select>
            </div>
            <div className="filtro-select">
              <select name="req_compra" value={filtros.req_compra} onChange={handleFiltroChange}>
                <option value="">Req. compra: todas</option>
                <option value="SI">Requieren compra</option>
                <option value="NO">No requieren compra</option>
              </select>
            </div>
            <div className="filtro-select">
              <select name="activo" value={filtros.activo} onChange={handleFiltroChange}>
                <option value="">Todos los estados</option>
                <option value="1">Activos</option>
                <option value="0">Inactivos</option>
              </select>
            </div>
            <button className="btn-limpiar" onClick={limpiarFiltros}>Limpiar</button>
          </div>

          {/* Incremento masivo */}
          <div className="matriz-bulk-incremento">
            <span className="bulk-label">Incremento masivo:</span>
            <input
              type="number"
              value={incrementoMasivo}
              onChange={(e) => setIncrementoMasivo(e.target.value)}
              placeholder="Ej: 0, 10, 15..."
              min="0"
              step="0.01"
              className="bulk-input"
            />
            <span className="bulk-suffix">%</span>
            <button
              className="btn-aplicar-incremento"
              onClick={aplicarIncrementoMasivo}
              disabled={aplicandoIncremento || incrementoMasivo === ''}
            >
              {aplicandoIncremento ? 'Aplicando...' : 'Aplicar a todas'}
            </button>
          </div>

          {/* Estadísticas */}
          <div className="matriz-stats">
            <span>Total: <strong>{total}</strong> actividades</span>
            <span>Página {page} de {totalPages}</span>
          </div>

          {/* Tabla */}
          {loading ? (
            <div className="matriz-loading">Cargando...</div>
          ) : actividades.length === 0 ? (
            <div className="matriz-empty">
              <span>📋</span>
              <p>No hay actividades registradas</p>
              <button onClick={() => { resetForm(); setVista('crear'); }}>+ Nueva Actividad</button>
            </div>
          ) : (
            <>
              <div className="matriz-tabla-wrapper">
                <table className="matriz-tabla">
                  <thead>
                    <tr>
                      <th>Código</th>
                      <th>Actividad</th>
                      <th>Categoría</th>
                      <th>Capítulo</th>
                      <th>Unidad</th>
                      <th>Valor Unitario</th>
                      <th>Cant. Pred.</th>
                      <th>Inc. %</th>
                      <th>Espacio</th>
                      <th>Subcategoría</th>
                      <th>FFE</th>
                      <th>Enchape</th>
                      <th>Req. Compra</th>
                      <th>Proveedor</th>
                      <th>F.Desp.</th>
                      <th>F.Mult.</th>
                      <th>Valor Final</th>
                      <th>Estado</th>
                      <th>Imagen</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {actividades.map(act => {
                      const esActivo = Number(act.activo) === 1;
                      return (
                        <tr key={act.id} className={!esActivo ? 'inactivo' : ''}>
                          <td className="codigo">{act.codigo}</td>
                          <td className="actividad">{act.actividad}</td>
                          <td className="categoria">
                            <span className="tag categoria-tag">{act.categoria}</span>
                          </td>
                          <td className="capitulo">
                            <span className="tag capitulo-tag">{act.capitulo}</span>
                          </td>
                          <td className="unidad">{act.unidad}</td>
                          <td className="valor-unitario">
                            {parseFloat(act.valor_unitario) > 0
                              ? `$${parseFloat(act.valor_unitario).toLocaleString('es-CO')}`
                              : <span className="sin-precio">Sin precio</span>
                            }
                          </td>
                          <td className="cant-pred">{act.cantidad_predeterminada || '1'}</td>
                          <td className="numero">{parseFloat(act.incremento_actividades) || 0}%</td>
                          <td className="espacio">{act.espacio}</td>
                          <td className="subcategoria">
                            {act.subcategoria_espacio || <span className="sin-subcategoria">—</span>}
                          </td>
                          <td className="ffe">
                            <span className={`ffe-badge ${act.ficha_ffe === 'SI' ? 'ffe-si' : 'ffe-no'}`}>
                              {act.ficha_ffe || 'NO'}
                            </span>
                          </td>
                          <td className="ffe">
                            <span className={`ffe-badge ${act.enchape === 'SI' ? 'ffe-si' : 'ffe-no'}`}>
                              {act.enchape || 'NO'}
                            </span>
                          </td>
                          <td className="ffe">
                            <span className={`ffe-badge ${act.req_compra === 'SI' ? 'ffe-si' : 'ffe-no'}`}>
                              {act.req_compra || 'NO'}
                            </span>
                          </td>
                          <td className="proveedor-cell">
                            <SelectorProveedor
                              valorId={act.proveedor_id || null}
                              valorNombre={act.proveedor_nombre || ''}
                              proveedores={proveedores}
                              guardando={guardandoProv === act.id}
                              onElegir={(pid, nombre) => asignarProveedor(act.id, pid, nombre)}
                            />
                          </td>
                          <td className="numero">{act.factor_desperdicio}</td>
                          <td className="numero">{act.factor_multiplicador}</td>
                          <td className="valor-final">
                            {parseFloat(act.valor_unitario) > 0
                              ? `$${Math.floor(calcularValorFinal(act.valor_unitario, act.incremento_actividades, act.factor_desperdicio, act.factor_multiplicador)).toLocaleString('es-CO')}`
                              : <span className="sin-precio">-</span>
                            }
                          </td>
                          <td className="estado">
                            <span className={`estado-badge ${esActivo ? 'activo' : 'inactivo'}`}>
                              {esActivo ? 'Activo' : 'Inactivo'}
                            </span>
                          </td>
                          <td className="imagen-rel">
                            {act.imagen_nombre ? (
                              <span className="imagen-rel-chip" title={act.imagen_nombre}>
                                <span className="imagen-rel-nombre">{act.imagen_nombre}</span>
                                <button className="imagen-rel-x" onClick={() => quitarImagenRelacionada(act.id)} title="Quitar imagen">✕</button>
                              </span>
                            ) : (
                              <span className="imagen-rel-vacio">—</span>
                            )}
                          </td>
                          <td className="acciones">
                            <button className="btn-icon" onClick={() => editarActividad(act)} title="Editar">✏️</button>
                            <button className="btn-icon" onClick={() => duplicarActividad(act)} title="Duplicar">📋</button>
                            <button className="btn-icon" onClick={() => setImagenModal({ id: act.id, codigo: act.codigo, actividad: act.actividad })} title={act.imagen_nombre ? 'Cambiar imagen relacionada' : 'Relacionar imagen'}>🖼️</button>
                            <button className="btn-icon delete" onClick={() => eliminarActividad(act.id)} title="Eliminar">🗑️</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Paginación */}
              <div className="matriz-paginacion">
                <button
                  disabled={page === 1}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                >
                  ← Anterior
                </button>
                <span>Página {page} de {totalPages}</span>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                >
                  Siguiente →
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Vista Crear/Editar */}
      {(vista === 'crear' || vista === 'editar') && (
        <div className="matriz-form-view">
          <div className="matriz-form-container">
            <h2>{pendienteEditando ? 'Corregir y reenviar a aprobación' : (vista === 'editar' ? 'Editar Actividad' : 'Nueva Actividad')}</h2>

            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Categoría *</label>
                  <select name="categoria" value={formData.categoria} onChange={handleChange} required>
                    <option value="">Seleccione...</option>
                    {listas.categorias.map(c => (
                      <option key={c.id} value={c.nombre}>{c.nombre}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Capítulo Presupuesto *</label>
                  <select name="capitulo" value={formData.capitulo} onChange={handleChange} required>
                    <option value="">Seleccione...</option>
                    {listas.capitulos.map(c => (
                      <option key={c.id} value={c.nombre}>{c.nombre}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Código * {!actividadEditando && <small style={{ color: '#6b7280', fontWeight: 400 }}>(automático)</small>}</label>
                  <input
                    type="text"
                    name="codigo"
                    value={formData.codigo}
                    onChange={handleChange}
                    required
                    readOnly={!actividadEditando && !pendienteEditando}
                    placeholder={(actividadEditando || pendienteEditando) ? 'Código' : 'Se genera al seleccionar categoría'}
                    style={(!actividadEditando && !pendienteEditando) ? { backgroundColor: '#f3f4f6', cursor: 'default' } : {}}
                  />
                </div>
                <div className="form-group">
                  <label>Unidad *</label>
                  <select name="unidad" value={formData.unidad} onChange={handleChange} required>
                    {listas.unidades.map(u => (
                      <option key={u.id} value={u.codigo}>{u.codigo} - {u.nombre}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Descripción de la Actividad *</label>
                <textarea
                  name="actividad"
                  value={formData.actividad}
                  onChange={handleChange}
                  required
                  rows="2"
                  placeholder="Descripción detallada de la actividad..."
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Valor Unitario</label>
                  <input
                    type="number"
                    name="valor_unitario"
                    value={formData.valor_unitario}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                    placeholder="Ej: 50000"
                  />
                  {formData.valor_unitario > 0 && (
                    <small className="valor-preview">${parseFloat(formData.valor_unitario).toLocaleString('es-CO')}</small>
                  )}
                </div>
                <div className="form-group">
                  <label>Cant. Predeterminada</label>
                  <input
                    type="text"
                    name="cantidad_predeterminada"
                    value={formData.cantidad_predeterminada}
                    onChange={handleChange}
                    placeholder="Ej: 1, property_size, bathrooms_area"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Espacio</label>
                  <select name="espacio" value={formData.espacio} onChange={handleChange}>
                    {listas.espacios.map(e => (
                      <option key={e.id} value={e.nombre}>{e.nombre}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Subcategoría de espacio</label>
                  {/* Texto libre; sugiere las que ya existen para escribirlas igual */}
                  <input
                    type="text"
                    name="subcategoria_espacio"
                    value={formData.subcategoria_espacio || ''}
                    onChange={handleChange}
                    list="matriz-subcategorias-espacio"
                    maxLength={150}
                    placeholder="Opcional"
                  />
                  <datalist id="matriz-subcategorias-espacio">
                    {(listas.subcategorias_espacio || []).map(s => <option key={s} value={s} />)}
                  </datalist>
                </div>
              </div>

              {/* Tres campos SI/NO en una misma fila */}
              <div className="form-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                <div className="form-group">
                  <label>Ficha FFE</label>
                  <select name="ficha_ffe" value={formData.ficha_ffe} onChange={handleChange}>
                    <option value="NO">NO</option>
                    <option value="SI">SI</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Enchape</label>
                  <select name="enchape" value={formData.enchape} onChange={handleChange}>
                    <option value="NO">NO</option>
                    <option value="SI">SI</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Req. Compra</label>
                  <select name="req_compra" value={formData.req_compra || 'NO'} onChange={handleChange}>
                    <option value="NO">NO</option>
                    <option value="SI">SI</option>
                  </select>
                </div>
              </div>

              <div className="form-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                <div className="form-group">
                  <label>Incremento (%)</label>
                  <input
                    type="number"
                    name="incremento_actividades"
                    value={formData.incremento_actividades}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label>Factor Desperdicio</label>
                  <input
                    type="number"
                    name="factor_desperdicio"
                    value={formData.factor_desperdicio}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label>Factor Multiplicador</label>
                  <input
                    type="number"
                    name="factor_multiplicador"
                    value={formData.factor_multiplicador}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                  />
                </div>
              </div>

              {formData.valor_unitario > 0 && (() => {
                const vu = parseFloat(formData.valor_unitario) || 0;
                const inc = parseFloat(formData.incremento_actividades) || 0;
                const desp = parseFloat(formData.factor_desperdicio) || 1;
                const mult = parseFloat(formData.factor_multiplicador) || 1;
                const conIncremento = vu * (1 + inc / 100);
                const conDesperdicio = conIncremento * desp;
                const valorFinal = Math.floor(conDesperdicio * mult);
                return (
                  <div className="valor-final-preview">
                    <strong>Valor Final: </strong>
                    <span>${valorFinal.toLocaleString('es-CO')}</span>
                    <small className="valor-final-detalle">
                      {`$${vu.toLocaleString('es-CO')}`}
                      {inc > 0 && ` + ${inc}% = $${Math.floor(conIncremento).toLocaleString('es-CO')}`}
                      {desp !== 1 && ` × ${desp} (desperdicio) = $${Math.floor(conDesperdicio).toLocaleString('es-CO')}`}
                      {mult !== 1 && ` × ${mult} (multiplicador) = $${valorFinal.toLocaleString('es-CO')}`}
                    </small>
                  </div>
                );
              })()}

              {/* Imagen relacionada: se puede adjuntar desde la creación,
                  antes solo se podía una vez creada la actividad. */}
              <div className="form-group">
                <label>Imagen relacionada</label>
                <div className="matriz-img-form">
                  {formData.imagen_nombre ? (
                    <span className="imagen-rel-chip" title={formData.imagen_nombre}>
                      <span className="imagen-rel-nombre">{formData.imagen_nombre}</span>
                      <button
                        type="button" className="imagen-rel-quitar" title="Quitar imagen"
                        onClick={() => setFormData(prev => ({ ...prev, imagen_url: '', imagen_nombre: '' }))}
                      >×</button>
                    </span>
                  ) : (
                    <span className="matriz-img-vacio">Sin imagen</span>
                  )}
                  <button type="button" className="matriz-img-btn" onClick={() => setImagenFormModal(true)}>
                    🖼️ {formData.imagen_nombre ? 'Cambiar' : 'Relacionar imagen'}
                  </button>
                </div>
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="activo"
                    checked={Number(formData.activo) === 1}
                    onChange={(e) => setFormData(prev => ({ ...prev, activo: e.target.checked ? 1 : 0 }))}
                  />
                  Actividad activa
                </label>
              </div>

              <div className="form-actions">
                <button type="button" className="btn-cancelar" onClick={() => { setVista('lista'); resetForm(); }}>
                  Cancelar
                </button>
                <button type="submit" className="btn-guardar" disabled={loading}>
                  {loading ? 'Guardando...' : (pendienteEditando ? 'Reenviar a aprobación' : (vista === 'editar' ? 'Actualizar' : 'Guardar'))}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: relacionar imagen del catálogo FFE a la actividad */}
      {imagenModal && (
        <div className="matriz-cat-modal-overlay" onClick={() => setImagenModal(null)}>
          <div className="matriz-cat-modal-content" onClick={e => e.stopPropagation()}>
            <div className="matriz-cat-modal-header">
              <h3>Relacionar imagen · {imagenModal.codigo}</h3>
              <button className="matriz-cat-modal-close" onClick={() => setImagenModal(null)}>&times;</button>
            </div>
            <div className="matriz-cat-modal-body">
              <CatalogoFFE
                selectionMode={true}
                bucket="catalogo-ffe"
                allowBucketSwitch={true}
                onSelectImage={(url, key, name) => {
                  guardarImagenRelacionada(imagenModal.id, url, name);
                  setImagenModal(null);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Selector de imagen del formulario (creación o corrección) */}
      {imagenFormModal && (
        <div className="matriz-cat-modal-overlay" onClick={() => setImagenFormModal(false)}>
          <div className="matriz-cat-modal-content" onClick={e => e.stopPropagation()}>
            <div className="matriz-cat-modal-header">
              <h3>Relacionar imagen{formData.codigo ? ` · ${formData.codigo}` : ''}</h3>
              <button className="matriz-cat-modal-close" onClick={() => setImagenFormModal(false)}>&times;</button>
            </div>
            <div className="matriz-cat-modal-body">
              <CatalogoFFE
                selectionMode={true}
                bucket="catalogo-ffe"
                allowBucketSwitch={true}
                onSelectImage={(url, key, name) => {
                  setFormData(prev => ({ ...prev, imagen_url: url || '', imagen_nombre: name || '' }));
                  setImagenFormModal(false);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MatrizActividades;
